import type {
  ComfortParam,
  EnergyPhase,
  HealthCheck,
  RoomOccupancy,
  Sky,
  Status,
} from '../protocol/types';
import { fmt } from '../lib/format';
import type { ConnectionStatus } from '../realtime/store';

/** Tutto il copy dei monitor sta qui: il server manda codici, il client compone le frasi. */

export const STATUS_LABEL: Record<Status, string> = {
  ok: 'In linea',
  warn: 'Da approfondire',
  crit: 'Attenzione',
  nd: 'Senza dati',
};

export const SKY_LABEL: Record<Sky, string> = {
  clear: 'sereno',
  partly_cloudy: 'poco nuvoloso',
  cloudy: 'nuvoloso',
  rain: 'pioggia',
  storm: 'temporale',
  fog: 'nebbia',
  snow: 'neve',
};

export const ENERGY_HEADLINE: Record<EnergyPhase, string> = {
  // Voce unica "Luce" (02/11/2026, proposta da approvare con Ettore: docs/superpowers/tono-di-voce-tiw.md): frasi corte, calde, vere.
  idle: 'L’ufficio riposa. Accesi solo frigo, rete e server.',
  opening: 'L’ufficio si sta svegliando.',
  working: 'Si lavora e i consumi tengono il ritmo.',
  closing: 'La giornata finisce, i consumi scendono.',
};

export const HEALTH_LABEL: Record<HealthCheck['key'], string> = {
  air: 'Aria',
  water: 'Acqua',
  hvac: 'Climatizzazione',
  electrical: 'Impianto elettrico',
  sensors: 'Sensori',
};

export function healthVerdict(c: HealthCheck): { verdict: string; detail: string } {
  const ref = c.reference ? ` · riferimento ${c.reference.source}: ${c.reference.label}` : '';
  // Un sensore non valutato non deve mai rassicurare (né allarmare): si dice che non è misurato.
  if (c.status === 'nd') {
    const feminine = c.key === 'air' || c.key === 'water' || c.key === 'hvac';
    return {
      verdict: c.key === 'sensors' ? 'Non verificati' : feminine ? 'Non misurata' : 'Non misurato',
      detail: c.key === 'water' ? 'Il sensore non risponde' : COMMON.noData,
    };
  }
  switch (c.key) {
    case 'air':
      return {
        verdict: c.status === 'ok' ? 'Pulita' : 'Da arieggiare',
        detail:
          c.values.pm25 == null ? COMMON.noData : `PM2.5 ${fmt(c.values.pm25, 0)} µg/m³${ref}`,
      };
    case 'water':
      return {
        verdict: c.values.leak ? 'Perdita rilevata' : 'Nessuna perdita',
        detail: `Sensore attivo${c.values.batteryPct != null ? ` · batteria ${fmt(c.values.batteryPct, 0)}%` : ''}`,
      };
    case 'hvac':
      return {
        verdict: c.values.on ? 'Accesa con l’ufficio occupato' : 'Spenta',
        detail:
          c.values.on && c.values.powerKw != null
            ? `Open Space: ${fmt(c.values.powerKw, 1)} kW`
            : 'Non sta consumando',
      };
    case 'electrical':
      return {
        verdict: c.status === 'ok' ? 'Tensione stabile' : 'Tensione fuori fascia',
        detail: c.values.voltageV == null ? COMMON.noData : `${fmt(c.values.voltageV, 0)} V${ref}`,
      };
    case 'sensors':
      return {
        verdict:
          c.values.online === c.values.total
            ? 'Tutti connessi'
            : c.values.total - c.values.online === 1
              ? '1 non risponde'
              : `${c.values.total - c.values.online} non rispondono`,
        detail: `${c.values.online} su ${c.values.total} inviano dati`,
      };
  }
  // Chiave non prevista dal contratto (server più nuovo del client): meglio un ripiego che uno schermo nero.
  return { verdict: COMMON.unavailable, detail: '' };
}

const PARAM_LABEL: Record<ComfortParam['key'], string> = {
  temperature: 'Temperatura',
  humidity: 'Umidità',
  illuminance: 'Luce',
  noise: 'Rumore',
  pm25: 'PM2.5',
  co2: 'CO₂',
};

const PARAM_VERDICT: Record<ComfortParam['key'], Record<ComfortParam['verdict'], string>> = {
  temperature: { low: 'Un po’ fresca', ok: 'Perfetta', high: 'Un po’ calda', off: 'Non misurata' },
  humidity: { low: 'Aria un po’ secca', ok: 'Ideale', high: 'Un po’ umida', off: 'Non misurata' },
  illuminance: {
    low: 'Un po’ scarsa',
    ok: 'Luce giusta',
    high: 'Molto luminoso',
    off: 'Luci spente',
  },
  noise: { low: 'Silenzio', ok: 'Tranquillo', high: 'Un po’ vivace', off: 'Non misurato' },
  pm25: { low: 'Pulita', ok: 'Pulita', high: 'Da arieggiare', off: 'Non misurata' },
  co2: { low: 'Fresca', ok: 'Buona', high: 'Da arieggiare', off: 'Non misurata' },
};

export function paramLabel(p: ComfortParam): string {
  return PARAM_LABEL[p.key] ?? p.key;
}

export function paramVerdict(p: ComfortParam): string {
  return PARAM_VERDICT[p.key]?.[p.verdict] ?? COMMON.unavailable;
}

export function comfortVerdict(index: number | null): string {
  if (index == null) return 'In valutazione';
  return index >= 85 ? 'Si lavora bene' : index >= 70 ? 'Quasi perfetto' : 'Si può fare meglio';
}

export const MOOD_LABELS = ['Alla grande', 'Bene', 'Così così', 'Stanco', 'Giornata no'] as const;

export function greeting(hour: number): string {
  return hour < 13 ? 'Buongiorno' : hour < 18 ? 'Buon pomeriggio' : 'Buona serata';
}

/* ---------- Guscio dell'app e componenti condivisi ---------- */

export const SCREEN_TITLE = {
  'monitor-a': 'ENERGIA & SALUTE',
  'monitor-b': 'COMFORT & PERSONE',
} as const;

export const SHELL = {
  notFound: {
    title: 'Schermo non trovato',
    detail: 'Usa /display/monitor-a, /display/monitor-b o /display/tablet',
  },
  notConfigured: {
    title: 'Display non configurato',
    detail: 'Manca la chiave del dispositivo (#k=…) generata dal portale.',
  },
  /** Solo in sviluppo: suggerisce cosa aggiungere all'indirizzo. */
  devHint: 'In sviluppo: aggiungi #k=demo all’indirizzo.',
  unauthorized: {
    title: 'Display non autorizzato',
    detail: 'La chiave è stata revocata o non è valida. Rigenerala dal portale TIW.',
  },
} as const;

export const BADGE: Record<
  ConnectionStatus,
  { tone: 'live' | 'warn' | 'crit' | 'muted'; text: string }
> = {
  live: { tone: 'live', text: 'LIVE' },
  polling: { tone: 'warn', text: 'AGGIORNAMENTO LENTO' },
  reconnecting: { tone: 'warn', text: 'RICONNESSIONE…' },
  connecting: { tone: 'muted', text: 'CONNESSIONE…' },
  idle: { tone: 'muted', text: '…' },
  unauthorized: { tone: 'crit', text: 'DISPLAY NON AUTORIZZATO' },
};

export const COMMON = {
  stale: 'dato non aggiornato',
  waiting: 'In attesa dei dati…',
  outside: 'fuori',
  unavailable: 'Nessun dato adesso',
  /** Il dato manca: una sola frase per tutti i pannelli, senza «non disponibile» da referto. */
  noData: 'Nessun dato adesso',
} as const;

export const ERR = {
  panel: 'Nessun dato adesso',
  root: {
    title: 'Qualcosa non ha funzionato',
    detail: 'La pagina si ricaricherà da sola tra un minuto.',
  },
} as const;

/* ---------- Monitor A ---------- */

export const A = {
  energyTitle: 'Quanta energia usiamo',
  updatedAgo: (s: number) => `aggiornato ${s} s fa`,
  ringLabel: 'Potenza assorbita adesso',
  inUse: 'in uso adesso',
  todayEnergy: 'Energia di oggi',
  todayCost: 'Costo stimato oggi',
  peakToday: 'Picco di oggi',
  breakdownTitle: 'Dove va l’energia di oggi',
  measured: 'misurato',
  derived: 'calcolato',
  chartTitle: 'La giornata, ora per ora',
  chartLabel: 'Potenza assorbita dall’ufficio durante la giornata',
  typicalProfile: 'giornata tipica',
  now: 'adesso',
  healthTitle: 'Come sta l’ufficio',
  healthOk: 'L’ufficio sta bene',
  healthKo: 'Qualcosa da guardare da vicino',
  healthSummary: (ok: number, total: number, ago: number | null) =>
    `${ok} ${ok === 1 ? 'controllo' : 'controlli'} su ${total} in linea${ago === null ? '' : ` · ultimo controllo ${ago} s fa`}`,
  factsLabel: 'LO SAPEVI?',
  footer:
    '“In linea” = valore entro il riferimento tecnico indicato. Non è un’attestazione di conformità. Metodo e fonti nel portale TIW.',
} as const;

/* ---------- Monitor B ---------- */

export const B = {
  comfortTitle: 'Come si sta qui',
  roomNow: (room: string) => `${room} · adesso`,
  ringLabel: 'Indice di comfort',
  outOf100: 'su 100',
  paramsInLine: (ok: number, total: number) =>
    `${ok} ${ok === 1 ? 'parametro' : 'parametri'} su ${total} in linea con i riferimenti tecnici`,
  estimateSuffix: ' (stima)',
  reference: 'Riferimento:',
  unavailable: 'Nessun dato adesso.',
  greeting: (g: string, client: string) => `${g} dal team ${client}`,
  moodTitle: 'L’umore del team',
  moodSub: 'Come va oggi? · risposte anonime dal tablet',
  moodRingLabel: 'Umore medio del team',
  moodOutOf: 'su 10',
  moodResponses: (n: number) =>
    n === 1 ? '1 risposta da stamattina' : `${n} risposte da stamattina`,
  moodMinimum: (min: number, n: number) =>
    `Ne bastano ${min} per vedere l’umore del team. Finora: ${n}.`,
  moodCta: 'Dicci come va: 20 secondi dal tablet.',
  occupancyTitle: 'Chi c’è in ufficio',
  peopleOf: (capacity: number) => `persone su ${capacity} posti`,
  /** Senza il conteggio delle persone si dice quante stanze sono in uso, mai «— persone». */
  roomsInUse: (used: number, total: number) =>
    `${used} ${used === 1 ? 'stanza in uso' : 'stanze in uso'} su ${total}`,
  occupancyPrivacy:
    'Contiamo le presenze senza identificare nessuno: niente telecamere, niente nomi.',
  yes: 'sì',
  no: 'no',
  inOutTitle: 'Dentro e fuori',
  inside: 'Dentro',
  outside: (city: string | undefined) => (city ? `Fuori · ${city}` : 'Fuori'),
  humidity: (v: string) => `umidità ${v}%`,
  coffeeTitle: 'Caffè di oggi',
  cupsSince: (t: string) => `tazzine dalle ${t}`,
  noCups: 'Il primo caffè deve ancora arrivare.',
  coffeeSource: 'Contati dalla presa smart della macchina.',
  busiest: (from: string, to: string) => `L’ora più richiesta: tra le ${from} e le ${to}.`,
  dayStarted: 'La giornata è appena iniziata.',
  footer:
    '“In linea” = valore entro il riferimento tecnico indicato (norme volontarie e linee guida). Non è un’attestazione di conformità.',
} as const;

const NOISE_LIKE = (db: number) =>
  db < 30
    ? 'quasi silenzio'
    : db < 42
      ? 'come in una biblioteca'
      : db < 50
        ? 'come un ufficio tranquillo'
        : 'come una conversazione animata';

const CAPTION: Record<ComfortParam['key'], Partial<Record<ComfortParam['verdict'], string>>> = {
  temperature: {
    ok: 'Né caldo né freddo per il lavoro alla scrivania.',
    low: 'Un po’ di freddo per lavorare alla scrivania.',
    high: 'Un po’ di caldo per lavorare alla scrivania.',
  },
  humidity: {
    ok: 'Né secca né afosa: bene per gola e occhi.',
    low: 'Aria secca: può irritare gola e occhi.',
    high: 'Aria umida: sensazione di afa.',
  },
  illuminance: {
    ok: 'Abbastanza per leggere e lavorare al PC.',
    low: 'Poca luce per leggere e lavorare al PC.',
    high: 'Molta luce: attenzione ai riflessi sullo schermo.',
    off: 'Luci spente: ufficio vuoto.',
  },
  noise: {},
  pm25: { ok: 'Aria pulita.', low: 'Aria pulita.', high: 'Conviene arieggiare.' },
  co2: { ok: 'Aria buona.', low: 'Aria fresca.', high: 'Conviene arieggiare.' },
};

/** Frase sotto il riquadro: la compone il client a partire da parametro, giudizio e valore. */
export function paramCaption(p: ComfortParam): string {
  if (p.key === 'noise')
    return p.value == null ? B.unavailable : `Adesso è ${NOISE_LIKE(p.value)}.`;
  return CAPTION[p.key]?.[p.verdict] ?? B.unavailable;
}

export function moodVerdict(score10: number): string {
  return score10 >= 7
    ? 'Oggi si sorride'
    : score10 >= 5
      ? 'Giornata nella media'
      : 'Giornata impegnativa';
}

/** Riga sotto il nome della stanza. */
export function roomNote(r: RoomOccupancy): string {
  if (r.count === null) return r.occupied ? 'occupata' : 'libera';
  if (r.activity === 'meeting') return 'riunione in corso';
  if (r.activity === 'empty' && r.count === 0) return 'libera, prenotabile';
  const free = Math.max(0, r.capacity - r.count);
  return free === 1 ? '1 posto libero' : `${free} posti liberi`;
}

/** `diff` = temperatura interna meno esterna; null se manca uno dei due dati. */
export function inOutNote(diff: number | null): string {
  if (diff === null || Number.isNaN(diff)) return '';
  if (diff > 0)
    return `Dentro ci sono ${fmt(diff, 1)} °C in più che fuori. Prendi la giacca prima di uscire.`;
  if (diff < 0) return 'Fuori fa più caldo che dentro: qui si sta freschi.';
  return 'Dentro e fuori c’è la stessa temperatura.';
}

/* ---------- Tablet ---------- */

export const T = {
  brand: 'TIW',
  back: 'Indietro',
  cancel: 'Esci',
  homeTitle: ['Accendi la luce', 'sul tuo ufficio.'],
  // 03/11/2026: la frase dice cosa fa TIW (prima diceva solo cosa non fa e come «ci prendiamo cura»).
  homeSub:
    'TIW misura comfort, energia e spazi dell’ufficio in tempo reale. Non misuriamo le persone.',
  /** Riga dal vivo sulla home: si compone solo con i dati che il server manda. */
  liveStrip: (parts: string[]) => `Adesso in ufficio: ${parts.join(' · ')}`,
  liveTemp: (value: string) => `${value} °C`,
  liveCoffee: (n: number) => `${n} caffè`,
  // 03/11/2026: «Quattro tocchi» era gergo: ora dice cosa si fa (4 domande) e cosa si ottiene.
  team: {
    title: 'Lavoro qui',
    hint: 'Rispondi a 4 domande, anonime. Poi vedi cosa pensa il team.',
  },
  // 03/11/2026: «con altri occhi» era astratto: ora dice cosa succede (tre domande, poi l'ufficio dal vivo).
  guest: {
    title: 'Sono un ospite',
    hint: '30 secondi: tre domande, poi guardi l’ufficio dal vivo.',
  },
  step: (n: number, total: number) => `Domanda ${n} di ${total}`,
  thanksTitle: 'Grazie!',
  thanksTeam: 'Ecco cosa dice il team, in forma anonima.',
  thanksTeamPlain:
    'La tua risposta è anonima e contribuisce ai risultati aggregati mostrati sul monitor.',
  thanksMood: 'Umore del team',
  thanksCoffee: 'Caffè di oggi',
  thanksCoffeeSub: 'il carburante del team',
  countdown: (n: number) => `Si torna all’inizio tra ${n} s`,
  done: 'Fine',
  privacy: 'Le risposte del team sono anonime e compaiono solo in forma aggregata.',
  privacyGuest: 'Nessun dato personale senza il tuo consenso.',
  privacyLink: 'Informativa privacy',
  offline: 'Connessione assente: le risposte non possono essere inviate.',
  sendError: 'Non siamo riusciti a inviare la risposta. Controlla la connessione e riprova.',
  demoError: 'Non siamo riusciti a registrare la richiesta. Riprova.',
  /* Percorso ospite */
  darkIntro: 'Il tuo ufficio, adesso. Lo sai…',
  darkYes: 'Sì',
  darkUnsure: 'Non saprei',
  awareAll: 'Sai rispondere a tutto.',
  awareSome: (n: number) => `«Non saprei», ${n} ${n === 1 ? 'volta' : 'volte'} su 3.`,
  awareTextAll: 'Complimenti. Ora immagina di saperlo in tempo reale, senza chiederlo a nessuno.',
  awareTextSome: 'Qualcosa lo intuisci. Il resto si gestisce al buio.',
  awareTextNone: 'Normale: senza dati, un ufficio si gestisce al buio.',
  lightSwitch: 'Accendi la luce',
  lightKicker: 'QUESTO UFFICIO, ADESSO',
  lightTitle: 'Ecco le risposte.',
  lightKnows: 'TIW lo sa.',
  lightNext: 'E adesso cosa faccio?',
  answerTeam: 'Il team oggi sta bene?',
  answerTeamText: 'Si sorride. Lo dicono i colleghi, in forma anonima.',
  answerTeamNone: 'Servono 5 risposte dei colleghi, in forma anonima.',
  answerEnergy: 'Quanta energia stiamo usando?',
  answerEnergyUnit: 'kWh oggi',
  // 02/11/2026: tolto «stanza per stanza» finché il backend non conferma che il dato arriva per stanza.
  answerEnergyText: 'Ogni kWh di oggi, in tempo reale.',
  answerSpaces: 'Quali spazi si usano davvero?',
  answerSpacesUnit: 'stanze occupate',
  answerSpacesText: 'Lo vedi adesso, senza telecamere e senza nomi.',
  doTitle: 'Con la luce accesa, vedi cosa fare.',
  doSub: 'Piccoli gesti, al momento giusto.',
  doNoCameras: 'Niente telecamere, nessun controllo. Solo luce su quello che conta.',
  doCta: 'Accendi la luce nel tuo ufficio',
  // 03/11/2026: tolti «Ci penso» e «Salta» su richiesta di Ettore: si esce con «Esci».
  contactTitle: 'Ti richiamiamo noi.',
  contactSub:
    // 03/11/2026: «tutti facoltativi» e «lascia almeno uno» si contraddicevano: ora una regola sola.
    'Compila solo quello che vuoi. Per essere ricontattato lascia almeno un’email o un telefono.',
  contactName: 'Nome',
  contactNamePh: 'Come ti chiami?',
  contactCompany: 'Azienda',
  contactCompanyPh: 'Dove lavori?',
  contactEmail: 'Email',
  contactEmailPh: 'nome@azienda.it',
  contactPhone: 'Telefono',
  contactPhonePh: '+39 …',
  // 03/11/2026: «manca qualcosa» non diceva cosa.
  contactEmailBad: 'Controlla l’email: non sembra completa.',
  contactPhoneBad: 'Controlla il numero: servono almeno 6 cifre.',
  contactConsent: (client: string) =>
    `Acconsento a essere ricontattato da ${client} per una presentazione di TIW.`,
  contactSend: 'Contattatemi',
  contactNeedConsent: 'Spunta il consenso per inviare',
  contactNeedReach: 'Lascia email o telefono',
  contactNeedValid: 'Correggi i recapiti per inviare',
  // 03/11/2026: tolta la metafora: la frase dice solo cosa succede adesso.
  guestDoneDemo: 'Ti ricontattiamo entro due giorni lavorativi.',
  guestDoneTitleDemo: (name: string) => (name ? `Grazie, ${name}.` : 'Grazie.'),
  guestDoneTitle: 'Grazie della visita.',
  guestDoneText: 'Quando vorrai vederci chiaro, TIW lo saprà.',
} as const;
